import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { User } from 'firebase/auth';
import authService, { UserProfile } from '../../services/authService';
import analyticsService from '../../services/analyticsService';

interface AuthPanelProps {
  onAuthStateChange?: (user: User | null, profile: UserProfile | null) => void;
}

export const AuthPanel: React.FC<AuthPanelProps> = ({ onAuthStateChange }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  
  // Use refs to avoid re-subscribing when callback/user changes
  const onAuthStateChangeRef = useRef(onAuthStateChange);
  const userRef = useRef<User | null>(null);
  
  useEffect(() => {
    onAuthStateChangeRef.current = onAuthStateChange;
  }, [onAuthStateChange]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      userRef.current = user;
      setUser(user);
      setIsLoading(false);
      onAuthStateChangeRef.current?.(user, authService.getUserProfile());
      
      // Track user authentication state
      if (user) {
        analyticsService.setUser(user.uid);
        analyticsService.setUserProperties({
          user_method: user.providerData[0]?.providerId || 'unknown',
          has_display_name: !!user.displayName,
          email_verified: user.emailVerified
        });
      }
    });

    // Listen to profile changes separately
    const unsubscribeProfile = authService.onProfileChange((profile) => {
      setProfile(profile);
      // Use userRef.current to get the current user, not stale closure value
      onAuthStateChangeRef.current?.(userRef.current, profile);
    });

    return () => {
      unsubscribe();
      unsubscribeProfile();
    };
  }, []); // Empty deps - only subscribe once

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      await authService.signInWithGoogle();
      analyticsService.trackLogin('google');
      setShowLogin(false);
    } catch (error) {
      setError('Failed to sign in with Google');
      analyticsService.trackError('auth_google_signin_failed', error instanceof Error ? error.message : 'Unknown error');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      if (isSignUp) {
        await authService.signUpWithEmail(email, password, displayName);
        // Immediately update profile state after signup
        setProfile(authService.getUserProfile());
        analyticsService.trackSignup('email');
      } else {
        await authService.signInWithEmail(email, password);
        analyticsService.trackLogin('email');
      }
      
      setShowLogin(false);
      setEmail('');
      setPassword('');
      setDisplayName('');
    } catch (error: any) {
      setError(error.message || 'Authentication failed');
      analyticsService.trackError(
        isSignUp ? 'auth_email_signup_failed' : 'auth_email_signin_failed', 
        error?.message || 'Unknown error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      await authService.signOut();
      analyticsService.trackLogout();
    } catch (error) {
      setError('Failed to sign out');
      analyticsService.trackError('auth_signout_failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowLogin(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Sign In
        </button>

        {showLogin && createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto" 
            style={{ zIndex: 10000 }}
            onClick={() => setShowLogin(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-md my-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {isSignUp ? 'Sign Up' : 'Sign In'}
                </h2>
                <button
                  onClick={() => setShowLogin(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or</span>
                  </div>
                </div>

                {isSignUp && (
                  <input
                    type="text"
                    placeholder="Display Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <button
                  onClick={handleEmailAuth}
                  disabled={isLoading || !email || !password}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSignUp ? 'Sign Up' : 'Sign In'}
                </button>

                <button
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="w-full text-sm text-blue-600 hover:text-blue-800"
                >
                  {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      <div className="flex items-center space-x-2">
        {profile?.photoURL && (
          <img
            src={profile.photoURL}
            alt="Profile"
            className="w-8 h-8 rounded-full"
          />
        )}
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            {profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User'}
          </div>
          <div className="text-gray-500 text-xs">
            {user?.email}
          </div>
        </div>
      </div>
      
      <button
        onClick={handleSignOut}
        disabled={isLoading}
        className="flex items-center gap-2 px-3 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Sign Out
      </button>
    </div>
  );
};